import { KeyboardArrowDown, PersonRounded } from "@mui/icons-material";
import { alpha, Badge, IconButton } from "@mui/material";
import {
  ExitOutline,
  Menu,
  NotificationsOutline,
  PersonOutline,
} from "@raresail/react-ionicons";
import { Avatar } from "antd";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";
import { Logo } from "../../assets/svg/Svgs";
import { primary10, PRIMARY_COLOR } from "../../constants/colors";
import {
  ACCOUNT,
  APP_BAR_HEIGHT,
  APP_NAME,
  NOTIFICATIONS,
} from "../../constants/constants";
import { API_NOTIFICATIONS } from "../../constants/apis";
import { resolveMediaUrl } from "../../constants/config";
import { capitalizeText } from "../../utils/helpers";
import { openDrawer } from "../../utils/modalUtils";
import { useAuth } from "../context/AuthContext";
import { useGlobalContext } from "../context/GlobalContext";
import { DropdownView } from "./DropdownView";
import useGet from "../../hooks/common/useGet";

export const AppBar = () => {
  const { title = APP_NAME } = useGlobalContext();
  const { user, logout } = useAuth();

  return (
    <Wrapper className="x100 whiteBg">
      <div className="flexNullCenter y100 gap15" id="content">
        <div id="mobile" className="animate">
          <IconButton
            size="small"
            onClick={() => {
              openDrawer({ component: "mobile" });
            }}
          >
            <Menu fontSize={22} />
          </IconButton>
        </div>

        <img alt="logo" src={Logo} width={25} height={25} />

        <TitleText
          key={title}
          className="bold textDark font16 capitalize flexGrow"
          data-aos="fade-in"
          data-aos-duration="1000"
        >
          {title}
        </TitleText>

        {/* ACTIONS */}
        <div className="flexNullCenter gap10">
          {/* Notification bell */}
          <NotificationBell />

          {/* Account dropdown */}
          <DropdownView
            items={[
              {
                label: <AccountCard data={user} alwaysShowInfo />,
              },
              {
                type: "divider",
              },
              {
                label: capitalizeText(ACCOUNT),
                icon: <PersonOutline />,
                to: ACCOUNT,
              },
              {
                label: "Logout",
                icon: <ExitOutline />,
                onClick: logout,
              },
            ]}
            component={
              <div>
                <AccountCard data={user} />
              </div>
            }
          />
        </div>
      </div>
    </Wrapper>
  );
};

// ─── Notification Bell ──────────────────────────────────────────
const NotificationBell = () => {
  const [unreadCount, setUnreadCount] = useState(0);
  const { loadData } = useGet();
  const { reload_notifications } = useGlobalContext();

  // Hit the dedicated /unread-count/ endpoint. The plain list endpoint
  // ignores ?is_read=false (no DRF filterset registered), so it would
  // return the user's TOTAL count and the badge would never decrement.
  const fetchCount = () => {
    loadData({
      api: `${API_NOTIFICATIONS}unread-count/`,
      onSuccess: (data: any) => {
        setUnreadCount(data?.unread_count ?? 0);
      },
    });
  };

  useEffect(() => {
    fetchCount();
    // Poll every 60 seconds
    const interval = setInterval(fetchCount, 60000);
    return () => clearInterval(interval);
  }, []);

  // Refetch when notifications are reloaded
  useEffect(() => {
    if (reload_notifications) fetchCount();
  }, [reload_notifications]);

  return (
    <Link to={`/${NOTIFICATIONS}`}>
      <IconButton
        size="small"
        sx={{
          bgcolor: primary10,
          color: PRIMARY_COLOR,
        }}
      >
        <Badge
          badgeContent={unreadCount}
          color="error"
          max={99}
          sx={{
            "& .MuiBadge-badge": {
              fontSize: 10,
              height: 16,
              minWidth: 16,
            },
          }}
        >
          <NotificationsOutline fontSize={17} />
        </Badge>
      </IconButton>
    </Link>
  );
};

// ─── Account Card ───────────────────────────────────────────────
const AccountCard = ({
  data,
  alwaysShowInfo,
}: {
  data: any;
  alwaysShowInfo?: boolean;
}) => {
  const { first_name, last_name = "", email, image } = data || {};
  const fullName = `${first_name || APP_NAME} ${last_name || ""}`;
  const photoSrc = image ? resolveMediaUrl(image) : undefined;

  return (
    <AccountWrapper
      className="flexNullCenter gap10 pointer"
      alwaysShowInfo={alwaysShowInfo}
    >
      <Avatar
        src={photoSrc}
        icon={!photoSrc && !first_name && <PersonRounded fontSize="small" />}
        style={{
          backgroundColor: photoSrc ? "transparent" : primary10,
          color: PRIMARY_COLOR,
        }}
      >
        {!photoSrc && <div className="semiBold font16">{first_name?.[0]}</div>}
      </Avatar>

      {/* INFO */}
      <div id="info">
        <div className="font12 textDark semiBold">{fullName}</div>
        <div className="font11 grey">{email}</div>
      </div>

      {/* ARROW */}
      {!alwaysShowInfo && <KeyboardArrowDown id="info" fontSize="small" />}
    </AccountWrapper>
  );
};

// ─── Styles ─────────────────────────────────────────────────────
const Wrapper = styled.nav`
  height: ${APP_BAR_HEIGHT};
  border-bottom: 1.5px solid ${alpha(PRIMARY_COLOR, 0.08)};
  position: fixed;
  z-index: 1000;

  #content {
    padding: 0 20px;
  }

  #mobile {
    display: none;
  }

  @media (max-width: 960px) {
    #mobile {
      display: block;
    }
  }
`;

const TitleText = styled.div`
  @media (max-width: 960px) {
  }
`;

const AccountWrapper = styled.div<{ alwaysShowInfo?: boolean }>`
  @media (max-width: 960px) {
    ${({ alwaysShowInfo }) =>
      !alwaysShowInfo &&
      `
    #info {
      display: none;
    }
    `}
  }
`;